import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Avatar, Input, Spin, Table, Typography } from 'antd'
import { Icon } from '@iconify/react'
import { debounce } from 'lodash'
import dayjs from 'dayjs'

import {
    fetchWorkloadProjectTimelog,
    fetchWorkloadUserTimelogList,
    closeWorkloadTimelogDrawer,
    openWorkloadTimelogDrawer,
    workloadState,
    setWorkloadFilters,
} from './workload.slice'
import {
    downloadWorkloadPdf,
    downloadSavedWorkloadPdf,
    saveWorkloadReport,
} from './workload.service'
import {
    fetchProjectsForClientModal,
    clientsState,
} from '../../clients/clients.slice'
import { getSupportUsers } from '../../talent/talent.service'
import { getDisplayName } from '../../talent/talent.utils'
import {
    buildDefaultWorkloadReportName,
    buildWorkloadReportFilters,
    formatLogDuration,
    getDefaultReportingDateRange,
    getInitials,
    getReportingDateRangeForPeriod,
    REPORTING_PERIOD,
    toReportingDateTimestamp,
} from './workload.utils'
import WorkloadLogProgress from './components/WorkloadLogProgress'
import WorkloadTimelogDetailPanel from './components/WorkloadTimelogDetailPanel'
import WorkloadFilters from './components/WorkloadFilters'
import WorkloadPeriodControls from './components/WorkloadPeriodControls'
import SaveWorkloadReportModal from './components/SaveWorkloadReportModal'
import { loginState } from '../../login/login.slice'
import { getReportsAccess } from '../../../helpers/reportsPermission'
import { showActionNotification } from '../../../components/notification'
import { PRIVATE_ROUTES } from '../../../routes'

import styles from './WorkloadDashboard.module.css'

const { Title } = Typography

const WORKLOAD_SCROLL_TARGET_ID = 'workload-scroll-container'
const USER_FILTER_PAGE_SIZE = 20

const WorkloadDashboard = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const {
        status,
        error,
        rows,
        search,
        totalCount,
        totalLog,
        isNext,
        projectIds,
        userIds,
        periodType,
        start,
        end,
        detail,
    } = useSelector(workloadState)
    const { userProfile } = useSelector(loginState)
    const reportsAccess = useMemo(
        () => getReportsAccess(userProfile),
        [userProfile]
    )
    const selectedMemberId = detail?.open ? detail?.user?._id : null
    const {
        clientModalProjectsListData = [],
        clientModalProjectPage,
        clientModalProjectSearch,
        clientModalIsNext,
        clientModalListStatus,
    } = useSelector(clientsState)
    const loadingMoreRef = useRef(false)
    const filterUserLoadingRef = useRef(false)
    const [downloading, setDownloading] = useState(false)
    const [saveOpen, setSaveOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [filterUsers, setFilterUsers] = useState([])
    const [filterUserSearch, setFilterUserSearch] = useState('')
    const [filterUserLoading, setFilterUserLoading] = useState(false)
    const [filterUserIsNext, setFilterUserIsNext] = useState(false)
    const [filterUserPage, setFilterUserPage] = useState(1)

    const isLoading = status === 'loading'
    const isLoadingMore = status === 'loadingMore'
    const showListLoader = rows.length === 0 && (isLoading || status === 'idle')

    const reportFilters = useMemo(
        () =>
            buildWorkloadReportFilters({
                start,
                end,
                projectIds,
                userIds,
                search,
                periodType,
            }),
        [start, end, projectIds, userIds, search, periodType]
    )

    const defaultReportName = useMemo(
        () => buildDefaultWorkloadReportName(start, end),
        [start, end]
    )

    const handleDownloadPdf = useCallback(async () => {
        if (downloading || !reportsAccess.canView) return
        if (start == null || end == null) {
            showActionNotification({
                type: 'error',
                message: 'Select a date range before downloading',
            })
            return
        }
        setDownloading(true)
        const result = await downloadWorkloadPdf({
            name: defaultReportName,
            filters: reportFilters,
        })
        setDownloading(false)
        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to download PDF',
            })
        }
    }, [
        downloading,
        reportsAccess.canView,
        start,
        end,
        defaultReportName,
        reportFilters,
    ])

    const handleSaveReport = useCallback(
        async ({ name, visibility, alsoDownload }) => {
            if (saving || !reportsAccess.canEdit) return
            if (start == null || end == null) {
                showActionNotification({
                    type: 'error',
                    message: 'Select a date range before saving',
                })
                return
            }
            setSaving(true)
            const saved = await saveWorkloadReport({
                name,
                filters: reportFilters,
                visibility,
            })
            if (!saved.success) {
                setSaving(false)
                showActionNotification({
                    type: 'error',
                    message: saved.message || 'Failed to save report',
                })
                return
            }

            showActionNotification({
                type: 'success',
                message: saved.message || 'Report saved',
            })

            if (alsoDownload && saved.data?._id) {
                const pdf = await downloadSavedWorkloadPdf(saved.data._id)
                if (!pdf.success) {
                    showActionNotification({
                        type: 'error',
                        message: pdf.message || 'Report saved, but PDF failed',
                    })
                }
            }

            setSaving(false)
            setSaveOpen(false)
        },
        [saving, reportsAccess.canEdit, start, end, reportFilters]
    )

    useEffect(() => {
        loadingMoreRef.current = isLoadingMore
    }, [isLoadingMore])

    const loadMoreRows = useCallback(() => {
        if (!isNext || loadingMoreRef.current || isLoading) return
        dispatch(fetchWorkloadUserTimelogList({ reset: false }))
    }, [dispatch, isNext, isLoading])

    const applyFiltersAndFetch = useCallback(
        (patch = {}) => {
            dispatch(
                setWorkloadFilters({
                    search: patch.search ?? search,
                    start: patch.start ?? start,
                    end: patch.end ?? end,
                    periodType: patch.periodType ?? periodType,
                    projectIds: patch.projectIds ?? projectIds,
                    userIds: patch.userIds ?? userIds,
                })
            )
            dispatch(fetchWorkloadUserTimelogList({ reset: true }))
            if (detail?.open && detail?.user?._id) {
                dispatch(fetchWorkloadProjectTimelog())
            }
        },
        [
            dispatch,
            search,
            start,
            end,
            periodType,
            projectIds,
            userIds,
            detail?.open,
            detail?.user?._id,
        ]
    )

    const handlePeriodChange = useCallback(
        (nextPeriod) => {
            if (nextPeriod === periodType) return

            let range
            if (nextPeriod === REPORTING_PERIOD.CUSTOM) {
                // Keep the visible range when switching into Custom.
                if (start != null && end != null) {
                    range = {
                        start: toReportingDateTimestamp(start, 'start'),
                        end: toReportingDateTimestamp(end, 'end'),
                    }
                } else {
                    range = getReportingDateRangeForPeriod(
                        REPORTING_PERIOD.CUSTOM
                    )
                }
            } else {
                // Day / Week / Month always snap to *today* — never reuse a
                // stale custom start (or a week that began in the prior month).
                range = getReportingDateRangeForPeriod(nextPeriod, dayjs())
            }

            applyFiltersAndFetch({
                periodType: nextPeriod,
                ...range,
            })
        },
        [applyFiltersAndFetch, periodType, start, end]
    )

    const handleCustomRangeChange = useCallback(
        (range) => {
            if (!range?.start || !range?.end) {
                applyFiltersAndFetch({
                    periodType: REPORTING_PERIOD.CUSTOM,
                    ...getReportingDateRangeForPeriod(REPORTING_PERIOD.CUSTOM),
                })
                return
            }
            applyFiltersAndFetch({
                periodType: REPORTING_PERIOD.CUSTOM,
                start: toReportingDateTimestamp(range.start, 'start'),
                end: toReportingDateTimestamp(range.end, 'end'),
            })
        },
        [applyFiltersAndFetch]
    )

    const handleViewTimelogDetail = useCallback(
        (record) => {
            if (!record?._id) return
            dispatch(openWorkloadTimelogDrawer(record))
            dispatch(fetchWorkloadProjectTimelog())
        },
        [dispatch]
    )

    const handleResetFilters = useCallback(() => {
        const defaults = getDefaultReportingDateRange()
        dispatch(closeWorkloadTimelogDrawer())
        dispatch(
            setWorkloadFilters({
                search: '',
                periodType: REPORTING_PERIOD.DAY,
                ...defaults,
                projectIds: [],
                userIds: [],
            })
        )
        dispatch(fetchWorkloadUserTimelogList({ reset: true }))
    }, [dispatch])

    useEffect(() => {
        const defaults = getDefaultReportingDateRange()
        dispatch(
            setWorkloadFilters({
                search: '',
                periodType: REPORTING_PERIOD.DAY,
                ...defaults,
                projectIds: [],
                userIds: [],
            })
        )
        dispatch(fetchWorkloadUserTimelogList({ reset: true }))
    }, [dispatch])

    const debouncedProjectSearch = useMemo(
        () =>
            debounce((value) => {
                dispatch(
                    fetchProjectsForClientModal({
                        skip: 0,
                        filter: value ? { search: value } : {},
                        limit: 10,
                        search: value,
                    })
                )
            }, 300),
        [dispatch]
    )

    useEffect(
        () => () => debouncedProjectSearch.cancel(),
        [debouncedProjectSearch]
    )

    const handleProjectFilterOpen = useCallback(
        (open) => {
            if (!open) return
            dispatch(
                fetchProjectsForClientModal({
                    skip: 0,
                    filter: {},
                    limit: 10,
                    search: '',
                })
            )
        },
        [dispatch]
    )

    const handleProjectFilterScroll = useCallback(
        (e) => {
            const { target } = e
            if (
                target.scrollHeight - target.scrollTop <=
                    target.clientHeight + 50 &&
                clientModalIsNext &&
                clientModalListStatus !== 'loading'
            ) {
                const nextPage = clientModalProjectPage + 1
                const skip = (nextPage - 1) * 10
                dispatch(
                    fetchProjectsForClientModal({
                        skip,
                        filter: clientModalProjectSearch
                            ? { search: clientModalProjectSearch }
                            : {},
                        limit: 10,
                        search: clientModalProjectSearch,
                    })
                )
            }
        },
        [
            dispatch,
            clientModalIsNext,
            clientModalListStatus,
            clientModalProjectPage,
            clientModalProjectSearch,
        ]
    )

    const loadFilterUsers = useCallback(
        async ({ skip = 0, search: searchTerm = '' } = {}) => {
            if (filterUserLoadingRef.current) return
            filterUserLoadingRef.current = true
            setFilterUserLoading(true)
            try {
                const { users: nextUsers, isNext: nextIsNext } =
                    await getSupportUsers({
                        skip,
                        limit: USER_FILTER_PAGE_SIZE,
                        search: searchTerm,
                    })

                setFilterUsers((prev) =>
                    skip > 0 ? [...prev, ...nextUsers] : nextUsers
                )
                setFilterUserIsNext(Boolean(nextIsNext))
                setFilterUserSearch(searchTerm)
                setFilterUserPage(
                    skip > 0 ? Math.floor(skip / USER_FILTER_PAGE_SIZE) + 1 : 1
                )
            } finally {
                filterUserLoadingRef.current = false
                setFilterUserLoading(false)
            }
        },
        []
    )

    const debouncedUserSearch = useMemo(
        () =>
            debounce((value) => {
                loadFilterUsers({ skip: 0, search: value || '' })
            }, 300),
        [loadFilterUsers]
    )

    useEffect(() => () => debouncedUserSearch.cancel(), [debouncedUserSearch])

    const handleUserFilterOpen = useCallback(
        (open) => {
            if (!open) return
            loadFilterUsers({ skip: 0, search: '' })
        },
        [loadFilterUsers]
    )

    const handleUserFilterScroll = useCallback(
        (e) => {
            const { target } = e
            if (
                target.scrollHeight - target.scrollTop <=
                    target.clientHeight + 50 &&
                filterUserIsNext &&
                !filterUserLoadingRef.current
            ) {
                loadFilterUsers({
                    skip: filterUsers.length,
                    search: filterUserSearch,
                })
            }
        },
        [
            filterUserIsNext,
            filterUsers.length,
            filterUserSearch,
            loadFilterUsers,
        ]
    )

    const userFilterOptions = useMemo(
        () =>
            (filterUsers || []).map((user) => {
                const displayName = getDisplayName(user)
                return {
                    _id: user?._id,
                    name:
                        displayName && displayName !== '—'
                            ? displayName
                            : user?.email || 'Untitled',
                    email: user?.email || '',
                    profilePicture: user?.profilePicture || '',
                }
            }),
        [filterUsers]
    )

    const debouncedFetch = useMemo(
        () =>
            debounce(() => {
                dispatch(fetchWorkloadUserTimelogList({ reset: true }))
            }, 350),
        [dispatch]
    )

    useEffect(() => () => debouncedFetch.cancel(), [debouncedFetch])

    const columns = useMemo(
        () => [
            {
                title: 'Team member',
                dataIndex: 'name',
                key: 'name',
                render: (_, record) => (
                    <div className={styles.userCell}>
                        <Avatar
                            size={36}
                            src={record?.profilePicture}
                            style={{
                                backgroundColor: '#6366f1',
                                flexShrink: 0,
                            }}
                        >
                            {!record?.profilePicture
                                ? getInitials(record?.name)
                                : null}
                        </Avatar>
                        <div className={styles.userMeta}>
                            <div className={styles.userName}>
                                {record?.name?.trim() || 'Unnamed'}
                            </div>
                            <div className={styles.userEmail}>
                                {record?.email || '—'}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                title: 'Total logged',
                dataIndex: 'totalLog',
                key: 'totalLog',
                width: 240,
                render: (_, record) => <WorkloadLogProgress record={record} />,
            },
            {
                title: 'Projects',
                dataIndex: 'projects',
                key: 'projects',
                render: (projects) => {
                    if (!projects?.length) {
                        return (
                            <span className={styles.emptyProjects}>
                                No project logs in range
                            </span>
                        )
                    }
                    return (
                        <div className={styles.projectsPillsWrap}>
                            {projects.map((p) => (
                                <span
                                    key={p?.projectId}
                                    className={styles.projectPill}
                                    title={p?.projectName || 'Project'}
                                >
                                    <span
                                        className={styles.projectSwatch}
                                        style={{
                                            backgroundColor:
                                                p?.colorCode || '#94a3b8',
                                        }}
                                    />
                                    <span className={styles.projectPillName}>
                                        {p?.projectName || 'Project'}
                                    </span>
                                    <span className={styles.projectLog}>
                                        {formatLogDuration(p?.totalLog)}
                                    </span>
                                </span>
                            ))}
                        </div>
                    )
                },
            },
            {
                title: '',
                key: 'view',
                width: 64,
                fixed: 'right',
                align: 'center',
                render: (_, record) => (
                    <button
                        type="button"
                        className={styles.viewBtn}
                        onClick={(e) => {
                            e.stopPropagation()
                            handleViewTimelogDetail(record)
                        }}
                        aria-label={`View timelog for ${
                            record?.name?.trim() || 'team member'
                        }`}
                        title="View timelog"
                    >
                        <Icon icon="mdi:eye-outline" width={18} height={18} />
                    </button>
                ),
            },
        ],
        [handleViewTimelogDetail]
    )

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.headerNavRow}>
                    <button
                        type="button"
                        className={styles.backLink}
                        onClick={() => navigate(PRIVATE_ROUTES.reporting)}
                    >
                        <Icon icon="mdi:arrow-left" width={18} height={18} />
                        All reports
                    </button>

                    <div className={styles.reportActions}>
                        {reportsAccess.canView ? (
                            <button
                                type="button"
                                className={styles.reportActionBtn}
                                onClick={() =>
                                    navigate(
                                        PRIVATE_ROUTES.reportingWorkloadSaved
                                    )
                                }
                            >
                                <Icon
                                    icon="mdi:folder-multiple-outline"
                                    width={16}
                                />
                                Saved reports
                            </button>
                        ) : null}
                        {reportsAccess.canView ? (
                            <button
                                type="button"
                                className={styles.reportActionBtn}
                                disabled={downloading}
                                onClick={handleDownloadPdf}
                            >
                                <Icon
                                    icon={
                                        downloading
                                            ? 'line-md:loading-loop'
                                            : 'mdi:download-outline'
                                    }
                                    width={16}
                                />
                                {downloading ? 'Downloading…' : 'Download'}
                            </button>
                        ) : null}
                        {reportsAccess.canEdit ? (
                            <button
                                type="button"
                                className={styles.reportActionPrimary}
                                onClick={() => setSaveOpen(true)}
                            >
                                <Icon
                                    icon="mdi:content-save-outline"
                                    width={16}
                                />
                                Save report
                            </button>
                        ) : null}
                    </div>
                </div>

                <div className={styles.headerTop}>
                    <div className={styles.headerText}>
                        <Title level={3} className={styles.title}>
                            Workload
                        </Title>
                        <p className={styles.subtitle}>
                            User workload and time logged across projects
                        </p>
                    </div>

                    <div className={styles.headerControls}>
                        <div className={styles.searchWrap}>
                            <Input
                                allowClear
                                placeholder="Search by name or email"
                                className={styles.search}
                                prefix={
                                    <Icon
                                        icon="mdi:magnify"
                                        width={18}
                                        height={18}
                                    />
                                }
                                value={search}
                                onChange={(e) => {
                                    dispatch(
                                        setWorkloadFilters({
                                            search: e.target.value,
                                        })
                                    )
                                    debouncedFetch()
                                }}
                            />
                        </div>

                        <WorkloadPeriodControls
                            periodType={periodType}
                            onPeriodChange={handlePeriodChange}
                        />

                        <WorkloadFilters
                            projectIds={projectIds}
                            projects={clientModalProjectsListData}
                            onProjectChange={(ids) =>
                                applyFiltersAndFetch({ projectIds: ids })
                            }
                            onProjectSearch={debouncedProjectSearch}
                            onProjectPopupScroll={handleProjectFilterScroll}
                            projectListLoading={
                                clientModalListStatus === 'loading'
                            }
                            projectListPage={clientModalProjectPage}
                            onProjectOpenChange={handleProjectFilterOpen}
                            userIds={userIds}
                            users={userFilterOptions}
                            onUserChange={(ids) =>
                                applyFiltersAndFetch({ userIds: ids })
                            }
                            onUserSearch={debouncedUserSearch}
                            onUserPopupScroll={handleUserFilterScroll}
                            userListLoading={filterUserLoading}
                            userListPage={filterUserPage}
                            onUserOpenChange={handleUserFilterOpen}
                            periodType={periodType}
                            start={start}
                            end={end}
                            onDateRangeChange={handleCustomRangeChange}
                            onClearFilters={handleResetFilters}
                        />
                    </div>

                    <div className={styles.summaryRow}>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>Members</div>
                            <div className={styles.summaryValue}>
                                {totalCount}
                            </div>
                        </div>
                        <div className={styles.summaryCard}>
                            <div className={styles.summaryLabel}>
                                Total logged
                            </div>
                            <div className={styles.summaryValue}>
                                {formatLogDuration(totalLog)}
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            <div className={styles.content}>
                {status === 'error' ? (
                    <div className={styles.errorState}>
                        <div className={styles.errorIconBadge} aria-hidden>
                            <Icon
                                icon="mdi:cloud-alert-outline"
                                width={26}
                                height={26}
                                className={styles.errorIcon}
                            />
                        </div>
                        <p className={styles.errorTitle}>
                            Unable to load reporting data
                        </p>
                        <p className={styles.errorMessage}>
                            {error || 'Something went wrong. Please try again.'}
                        </p>
                        <button
                            type="button"
                            className={styles.errorRetryBtn}
                            onClick={() =>
                                dispatch(
                                    fetchWorkloadUserTimelogList({
                                        reset: true,
                                    })
                                )
                            }
                        >
                            <Icon icon="mdi:refresh" width={16} />
                            Try again
                        </button>
                    </div>
                ) : showListLoader ? (
                    <div className={styles.listLoader} role="status">
                        <Icon
                            icon="line-md:loading-loop"
                            style={{ fontSize: 48, color: '#6366f1' }}
                            aria-hidden
                        />
                        <p className={styles.listLoaderText}>
                            Loading workload…
                        </p>
                    </div>
                ) : (
                    <>
                        <div
                            id={WORKLOAD_SCROLL_TARGET_ID}
                            className={styles.masterPane}
                        >
                            <Spin
                                spinning={isLoading}
                                className={styles.listSpin}
                            >
                                <InfiniteScroll
                                    dataLength={rows.length}
                                    next={loadMoreRows}
                                    hasMore={isNext}
                                    initialLoad={false}
                                    scrollThreshold="200px"
                                    scrollableTarget={WORKLOAD_SCROLL_TARGET_ID}
                                    loader={
                                        isLoadingMore ? (
                                            <div
                                                className={styles.scrollLoader}
                                            >
                                                <Icon
                                                    icon="line-md:loading-loop"
                                                    style={{
                                                        fontSize: 26,
                                                        color: '#6366f1',
                                                    }}
                                                />
                                            </div>
                                        ) : null
                                    }
                                >
                                    <div className={styles.tableWrap}>
                                        <Table
                                            rowKey="_id"
                                            columns={columns}
                                            dataSource={rows}
                                            pagination={false}
                                            scroll={{ x: 900 }}
                                            rowClassName={(record) =>
                                                record?._id === selectedMemberId
                                                    ? styles.tableRowSelected
                                                    : ''
                                            }
                                            onRow={(record) => ({
                                                onClick: () =>
                                                    handleViewTimelogDetail(
                                                        record
                                                    ),
                                                style: { cursor: 'pointer' },
                                            })}
                                            locale={{
                                                emptyText: (
                                                    <div
                                                        className={
                                                            styles.tableEmptyState
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.tableEmptyIconBadge
                                                            }
                                                            aria-hidden
                                                        >
                                                            <Icon
                                                                icon="mdi:clock-time-four-outline"
                                                                width={28}
                                                                height={28}
                                                                className={
                                                                    styles.tableEmptyIcon
                                                                }
                                                            />
                                                        </div>
                                                        <p
                                                            className={
                                                                styles.tableEmptyTitle
                                                            }
                                                        >
                                                            No timelog data for
                                                            this period
                                                        </p>
                                                        <p
                                                            className={
                                                                styles.tableEmptyHint
                                                            }
                                                        >
                                                            No team members
                                                            logged time in the
                                                            selected range. Try
                                                            widening the date
                                                            range or clearing
                                                            project filters.
                                                        </p>
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.tableEmptyAction
                                                            }
                                                            onClick={
                                                                handleResetFilters
                                                            }
                                                        >
                                                            <Icon
                                                                icon="mdi:filter-off-outline"
                                                                width={16}
                                                            />
                                                            Reset filters
                                                        </button>
                                                    </div>
                                                ),
                                            }}
                                        />
                                    </div>
                                </InfiniteScroll>
                            </Spin>
                        </div>

                        <WorkloadTimelogDetailPanel />
                    </>
                )}
            </div>

            <SaveWorkloadReportModal
                open={saveOpen}
                defaultName={defaultReportName}
                confirmLoading={saving}
                onCancel={() => {
                    if (!saving) setSaveOpen(false)
                }}
                onSubmit={handleSaveReport}
            />
        </div>
    )
}

export default WorkloadDashboard
